/* Error reporting — because a user hitting a bug is otherwise invisible.
 *
 * The app is deployed (#40). Until now the only way anyone learned that
 * something threw in a stranger's browser was if that stranger said so, which
 * nobody does; they close the tab.
 *
 * ---------------------------------------------------------------------------
 * THIS SENDS DATA OFF THE MACHINE. Three rules it must not break.
 *
 * 1. **The URL fragment never leaves.** It is not cosmetic: a share link
 *    carries an entire encoded deck in `location.hash` (#45), and the Supabase
 *    OAuth round trip briefly parks `#access_token=…` there. Reporting a raw
 *    URL would ship somebody's session token to a third party. `scrubUrl`
 *    strips every fragment on every event, breadcrumb and request, and
 *    `test/telemetry.test.mjs` asserts it against a real token-shaped hash.
 *
 * 2. **Off unless configured.** No DSN, no network calls, no SDK download —
 *    the import is dynamic so an unconfigured build never even fetches it.
 *    That is what `npm test` and the e2e suite run against.
 *
 * 3. **No card data, no deck contents, no email.** `sendDefaultPii` stays
 *    false. What is wanted here is a stack trace, not an audience.
 * ---------------------------------------------------------------------------
 *
 * The SDK is bundled from npm rather than loaded from Sentry's CDN, which is
 * the whole reason `script-src` is still `'self'` (#17). Only `connect-src`
 * grows, and by a wildcard on the ingest subdomain so the DSN can change
 * without touching the policy.
 */

/* Errors thrown before the SDK finishes loading are exactly the ones worth
 * having — a boot failure is invisible to everything else. These handlers are
 * registered synchronously; whatever they catch is replayed once Sentry is up,
 * and dropped if it never is. */
const buffered = [];
let ready = null;
let installed = false;

/** Cap it: a page throwing in a loop must not become an unbounded array. */
const MAX_BUFFERED = 20;

export function installErrorBuffer(target = globalThis) {
  if (installed) return;
  installed = true;

  const push = (kind, value) => {
    if (ready) { ready.capture(kind, value); return; }
    if (buffered.length < MAX_BUFFERED) buffered.push([kind, value]);
  };

  target.addEventListener?.('error', e => push('error', e?.error || e?.message));
  target.addEventListener?.('unhandledrejection', e => push('rejection', e?.reason));
}

/** Test seam — the buffer is module state and would otherwise leak between tests. */
export function _resetTelemetry() {
  buffered.length = 0;
  ready = null;
  installed = false;
}

/**
 * Does this look like a Sentry DSN rather than some other Sentry URL?
 *
 * `https://<publicKey>@<host>/<projectId>` — the `@` and the numeric path are
 * what distinguish it from the loader script, the dashboard URL, or an ingest
 * endpoint, all of which are things a person reasonably has in the clipboard
 * while setting this up.
 */
export function isDsn(value) {
  return /^https?:\/\/[^@/\s]+@[^/\s]+\/\d+$/.test(String(value ?? '').trim());
}

/**
 * Strip the fragment from a URL, whatever shape it is in.
 *
 * Deliberately string-first: `new URL` throws on the relative and partial URLs
 * that turn up in breadcrumbs, and a throw here would take out the error
 * report it was trying to clean.
 */
export function scrubUrl(url) {
  if (typeof url !== 'string') return url;
  const cut = url.indexOf('#');
  return cut === -1 ? url : `${url.slice(0, cut)}#[redacted]`;
}

/** Walk an event and scrub every URL-shaped field. */
export function scrubEvent(event) {
  if (!event || typeof event !== 'object') return event;

  if (event.request?.url) event.request.url = scrubUrl(event.request.url);
  if (event.request?.headers?.Referer) event.request.headers.Referer = scrubUrl(event.request.headers.Referer);
  if (Array.isArray(event.breadcrumbs)) {
    for (const b of event.breadcrumbs) {
      if (b?.data?.url) b.data.url = scrubUrl(b.data.url);
      if (b?.data?.to) b.data.to = scrubUrl(b.data.to);
      if (b?.data?.from) b.data.from = scrubUrl(b.data.from);
    }
  }
  for (const v of event.exception?.values || []) {
    for (const f of v?.stacktrace?.frames || []) {
      if (f?.filename) f.filename = scrubUrl(f.filename);
      if (f?.abs_path) f.abs_path = scrubUrl(f.abs_path);
    }
  }
  return event;
}

/** Same treatment on the way in, so a fragment never reaches a breadcrumb. */
export function scrubBreadcrumb(crumb) {
  if (crumb?.data?.url) crumb.data.url = scrubUrl(crumb.data.url);
  if (crumb?.data?.to) crumb.data.to = scrubUrl(crumb.data.to);
  if (crumb?.data?.from) crumb.data.from = scrubUrl(crumb.data.from);
  if (typeof crumb?.message === 'string') crumb.message = scrubUrl(crumb.message);
  return crumb;
}

/**
 * Start reporting, if there is somewhere to report to.
 *
 * @param {object} [opts]
 *   dsn          Sentry DSN; falsy disables everything
 *   release      version string, for grouping across deploys
 *   environment  'production' / 'development'
 *   load         override the SDK import, for tests
 * @returns {Promise<boolean>} whether it actually started
 */
export async function initTelemetry({
  dsn = import.meta.env?.VITE_SENTRY_DSN,
  release = import.meta.env?.VITE_RELEASE,
  environment = import.meta.env?.MODE,
  load = () => import('@sentry/browser'),
} = {}) {
  if (!dsn) return false;

  /* A DSN is `https://<key>@<host>/<projectId>`, and anything else disables
   * reporting *silently* — `Sentry.init` neither throws nor logs outside debug
   * mode, so the SDK downloads, the page looks instrumented and no event is
   * ever sent.
   *
   * This is not hypothetical. Sentry's browser setup page leads with a loader
   * script URL (`https://js.sentry-cdn.com/<key>.min.js`), which looks enough
   * like a DSN to be pasted into the variable — and production spent a deploy
   * that way before anyone noticed, because there is nothing to notice. */
  if (!isDsn(dsn)) {
    console.warn('Ignoring VITE_SENTRY_DSN: not a DSN (expected https://<key>@<host>/<projectId>). '
      + 'A loader-script URL is a common mistake here. Error reporting is OFF.');
    return false;
  }

  let Sentry;
  try {
    Sentry = await load();
  } catch {
    /* An SDK that fails to load must not be the reason the app does not
     * start. This is the one module in the project allowed to fail silently. */
    return false;
  }

  try {
    Sentry.init({
      dsn,
      release: release || undefined,
      environment: environment || 'production',

      /* Off. This is the switch that would attach IP addresses and request
       * bodies, and the point of this file is a stack trace. */
      sendDefaultPii: false,

      /* No performance tracing and no session replay: both are sampled volume
       * against a free-tier quota, and neither answers "what threw". Turn one
       * on deliberately if there is ever a question it would answer. */
      tracesSampleRate: 0,

      beforeSend: scrubEvent,
      beforeBreadcrumb: scrubBreadcrumb,

      /* Noise that is not ours and cannot be fixed here: an extension
       * injecting a script, a network that dropped mid-fetch, a tab closed
       * during a request. Each of these otherwise arrives daily and buries the
       * one report that matters. */
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        /^Non-Error promise rejection captured/,
        /Failed to fetch/i,
        /NetworkError when attempting to fetch/i,
        /Load failed/,
      ],
    });
  } catch {
    return false;
  }

  ready = {
    capture(kind, value) {
      try {
        if (kind === 'rejection') Sentry.captureException(value ?? new Error('Unhandled rejection'));
        else if (value instanceof Error) Sentry.captureException(value);
        else if (value != null) Sentry.captureMessage(String(value));
      } catch { /* reporting must never throw into the app */ }
    },
  };

  /* Replay whatever broke before the SDK arrived. */
  for (const [kind, value] of buffered.splice(0)) ready.capture(kind, value);
  return true;
}

/** Report something the app caught itself and handled. No-op when disabled. */
export function report(err) {
  ready?.capture('error', err);
}
